<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { DirectUpload } from 'activestorage';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const IMAGE_SIZE_LIMIT = 8 * 1024 * 1024;
const AUDIO_SIZE_LIMIT = 25 * 1000 * 1000;
const ATTACHMENT_SIZE_LIMITS = {
  'image/png': IMAGE_SIZE_LIMIT,
  'image/jpeg': IMAGE_SIZE_LIMIT,
  'audio/mpeg': AUDIO_SIZE_LIMIT,
  'audio/mp4': AUDIO_SIZE_LIMIT,
  'audio/x-m4a': AUDIO_SIZE_LIMIT,
  'audio/ogg': AUDIO_SIZE_LIMIT,
  'audio/wav': AUDIO_SIZE_LIMIT,
  'audio/x-wav': AUDIO_SIZE_LIMIT,
  'audio/webm': AUDIO_SIZE_LIMIT,
};
const AUDIO_ERROR_KEYS = {
  transcription_disabled: 'TRANSCRIPTION_DISABLED',
  audio_too_large: 'AUDIO_TOO_LARGE',
  empty_transcription: 'EMPTY_TRANSCRIPTION',
};

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const fileInputRef = ref(null);
const pendingAttachment = ref(null);
const isUploadingAttachment = ref(false);

const canRecordAudio =
  typeof MediaRecorder !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia;
const isRecording = ref(false);
const recordingSeconds = ref(0);
let recorder = null;
let recordingStream = null;
let recordingTimer = null;

const recordingLabel = computed(() => {
  const minutes = Math.floor(recordingSeconds.value / 60);
  const seconds = String(recordingSeconds.value % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
});

const formatMessagesForApi = () => {
  return messages.value
    .filter(message => message.sender !== 'system')
    .map(message => {
      const payload = {
        role: message.sender,
        // A voice message is carried in the history by its transcription.
        content: [message.content, message.transcription]
          .filter(Boolean)
          .join('\n'),
      };

      if (message.sender === 'assistant' && message.agentName) {
        payload.agent_name = message.agentName;
      }

      if (message.sender === 'assistant' && message.sharedContent?.faq_id) {
        payload.shared_faq_response_id = message.sharedContent.faq_id;
      }

      return payload;
    });
};

const resetConversation = () => {
  messages.value = [];
  newMessage.value = '';
  pendingAttachment.value = null;
};

// Watch for assistant ID changes and reset conversation
watch(
  () => assistantId,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      resetConversation();
    }
  }
);

const openFileDialog = () => {
  nextTick(() => fileInputRef.value?.click());
};

const removePendingAttachment = () => {
  pendingAttachment.value = null;
};

const uploadAttachment = file => {
  const mimeType = file.type.split(';')[0];
  if (!ATTACHMENT_SIZE_LIMITS[mimeType]) {
    useAlert(t('CAPTAIN.PLAYGROUND.ATTACHMENT.INVALID_TYPE'));
    return;
  }
  if (file.size > ATTACHMENT_SIZE_LIMITS[mimeType]) {
    useAlert(t('CAPTAIN.PLAYGROUND.ATTACHMENT.TOO_LARGE'));
    return;
  }

  isUploadingAttachment.value = true;
  const upload = new DirectUpload(
    file,
    CaptainAssistant.playgroundDirectUploadUrl(assistantId),
    {
      directUploadWillCreateBlobWithXHR: xhr => {
        xhr.setRequestHeader(
          'api_access_token',
          currentUser.value.access_token
        );
      },
    }
  );

  upload.create((error, blob) => {
    isUploadingAttachment.value = false;
    if (error) {
      useAlert(t('CAPTAIN.PLAYGROUND.ATTACHMENT.UPLOAD_ERROR'));
      return;
    }
    pendingAttachment.value = {
      signedId: blob.signed_id,
      filename: file.name,
      previewUrl: URL.createObjectURL(file),
      isAudio: mimeType.startsWith('audio/'),
    };
  });
};

const handleFileChange = event => {
  const file = event.target.files[0];
  event.target.value = '';
  if (file) uploadAttachment(file);
};

const clearRecordingTimer = () => {
  clearInterval(recordingTimer);
  recordingTimer = null;
};

const releaseRecordingStream = () => {
  recordingStream?.getTracks().forEach(track => track.stop());
  recordingStream = null;
};

const startRecording = async () => {
  if (isRecording.value || isUploadingAttachment.value) return;

  try {
    recordingStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
  } catch {
    useAlert(t('CAPTAIN.PLAYGROUND.AUDIO.MIC_DENIED'));
    return;
  }

  const chunks = [];
  recorder = new MediaRecorder(recordingStream);
  recorder.ondataavailable = event => chunks.push(event.data);
  recorder.onstop = () => {
    releaseRecordingStream();
    const mimeType = (recorder.mimeType || 'audio/webm').split(';')[0];
    const extension =
      { 'audio/mp4': 'm4a', 'audio/ogg': 'ogg' }[mimeType] || 'webm';
    uploadAttachment(
      new File(chunks, `gravacao.${extension}`, { type: mimeType })
    );
  };
  recorder.start();
  recordingSeconds.value = 0;
  isRecording.value = true;
  recordingTimer = setInterval(() => {
    recordingSeconds.value += 1;
  }, 1000);
};

const stopRecording = () => {
  if (!isRecording.value) return;

  isRecording.value = false;
  clearRecordingTimer();
  recorder.stop();
};

onBeforeUnmount(() => {
  clearRecordingTimer();
  if (recorder?.state === 'recording') recorder.stop();
  releaseRecordingStream();
});

const sendMessage = async () => {
  const hasContent = newMessage.value.trim() || pendingAttachment.value;
  if (!hasContent || isLoading.value || isUploadingAttachment.value) return;

  const userMessage = {
    content: newMessage.value,
    sender: 'user',
    timestamp: new Date().toISOString(),
    attachmentPreviewUrl: pendingAttachment.value?.previewUrl,
    attachmentIsAudio: pendingAttachment.value?.isAudio,
  };
  messages.value.push(userMessage);
  // Mutating the reactive proxy, not the plain object, so the UI updates.
  const sentMessage = messages.value[messages.value.length - 1];
  const currentMessage = newMessage.value;
  const attachmentBlobSignedId = pendingAttachment.value?.signedId;
  newMessage.value = '';
  pendingAttachment.value = null;

  try {
    isLoading.value = true;
    const { data } = await CaptainAssistant.playground({
      assistantId,
      messageContent: currentMessage,
      messageHistory: formatMessagesForApi(),
      attachmentBlobSignedId,
    });
    sentMessage.transcription = data.transcription;

    if (data.auto_resolved) {
      messages.value.push({
        sender: 'system',
        type: 'auto_resolved',
        timestamp: new Date().toISOString(),
      });
      return;
    }

    if (data.response === 'conversation_handoff') {
      if (data.handoff_message) {
        messages.value.push({
          content: data.handoff_message,
          sender: 'assistant',
          agentName: data.agent_name,
          timestamp: new Date().toISOString(),
        });
      }
      messages.value.push({
        sender: 'system',
        type: 'handoff',
        timestamp: new Date().toISOString(),
      });
      return;
    }

    const toolCalls = data.tool_calls || [];
    const blocks = data.response_blocks?.length
      ? data.response_blocks
      : [data.response];
    blocks.forEach((block, index) => {
      messages.value.push({
        content: block,
        sender: 'assistant',
        agentName: data.agent_name,
        toolCalls: index === 0 ? toolCalls : [],
        sharedContent: index === blocks.length - 1 ? data.shared_content : null,
        timestamp: new Date().toISOString(),
      });
    });
  } catch (error) {
    if (userMessage.attachmentIsAudio && error.response?.status === 422) {
      messages.value = messages.value.filter(
        message => message !== sentMessage
      );
      const errorKey =
        AUDIO_ERROR_KEYS[error.response.data?.error] || 'GENERIC';
      useAlert(t(`CAPTAIN.PLAYGROUND.AUDIO.ERRORS.${errorKey}`));
    } else {
      // eslint-disable-next-line no-console
      console.error('Error getting assistant response:', error);
    }
  } finally {
    isLoading.value = false;
  }
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  sendMessage();
};

const showSharePanel = ref(false);
const shareUrl = ref('');
const isGeneratingLink = ref(false);

const toggleSharePanel = () => {
  showSharePanel.value = !showSharePanel.value;
};

const generateShareLink = async () => {
  try {
    isGeneratingLink.value = true;
    const { data } = await CaptainAssistant.enableSharing(assistantId);
    shareUrl.value = data.share_url;
  } catch {
    useAlert(t('CAPTAIN.PLAYGROUND.SHARING.ERROR'));
  } finally {
    isGeneratingLink.value = false;
  }
};

const copyShareLink = async () => {
  try {
    await navigator.clipboard.writeText(shareUrl.value);
    useAlert(t('CAPTAIN.PLAYGROUND.SHARING.LINK_COPIED'));
  } catch {
    // fallback
  }
};

const disableSharing = async () => {
  try {
    await CaptainAssistant.disableSharing(assistantId);
    shareUrl.value = '';
    useAlert(t('CAPTAIN.PLAYGROUND.SHARING.DISABLE_SUCCESS'));
  } catch {
    // silent
  }
};
</script>

<template>
  <div
    class="flex flex-col h-full rounded-xl border py-6 border-n-weak text-n-slate-11"
  >
    <div class="mb-8 px-6">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('CAPTAIN.PLAYGROUND.HEADER') }}
        </h3>
        <div class="flex items-center gap-1">
          <NextButton
            ghost
            sm
            slate
            icon="i-lucide-share-2"
            @click="toggleSharePanel"
          />
          <NextButton
            ghost
            sm
            slate
            icon="i-lucide-rotate-ccw"
            @click="resetConversation"
          />
        </div>
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-if="showSharePanel"
      class="mx-6 mb-4 p-4 rounded-lg border border-n-weak bg-n-alpha-2"
    >
      <p class="text-sm text-n-slate-11 mb-3">
        {{ t('CAPTAIN.PLAYGROUND.SHARING.DESCRIPTION') }}
      </p>
      <div v-if="shareUrl" class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <input
            :value="shareUrl"
            readonly
            class="flex-1 text-sm bg-n-background border border-n-weak rounded-lg px-3 py-2 text-n-slate-12"
          />
          <NextButton sm icon="i-lucide-copy" @click="copyShareLink">
            {{ t('CAPTAIN.PLAYGROUND.SHARING.COPY_LINK') }}
          </NextButton>
        </div>
        <NextButton ghost sm ruby @click="disableSharing">
          {{ t('CAPTAIN.PLAYGROUND.SHARING.DISABLE') }}
        </NextButton>
      </div>
      <NextButton
        v-else
        sm
        :is-loading="isGeneratingLink"
        @click="generateShareLink"
      >
        {{ t('CAPTAIN.PLAYGROUND.SHARING.GENERATE_LINK') }}
      </NextButton>
    </div>

    <MessageList :messages="messages" :is-loading="isLoading" />

    <div
      v-if="pendingAttachment"
      class="flex items-center gap-2 mx-6 mb-2 px-3 py-2 rounded-lg bg-n-alpha-2"
    >
      <audio
        v-if="pendingAttachment.isAudio"
        :src="pendingAttachment.previewUrl"
        controls
        class="flex-1 h-8 min-w-0"
      />
      <template v-else>
        <img
          :src="pendingAttachment.previewUrl"
          class="object-cover rounded size-8"
        />
        <span class="flex-1 text-xs truncate text-n-slate-12">
          {{ pendingAttachment.filename }}
        </span>
      </template>
      <NextButton
        ghost
        xs
        slate
        icon="i-lucide-x"
        @click="removePendingAttachment"
      />
    </div>

    <div
      class="flex items-center mx-6 bg-n-background outline outline-1 outline-n-weak rounded-xl p-3"
    >
      <input
        ref="fileInputRef"
        type="file"
        accept=".png,.jpg,.jpeg,.mp3,.m4a,.ogg,.wav,.webm"
        class="hidden"
        @change="handleFileChange"
      />
      <NextButton
        ghost
        sm
        slate
        :is-loading="isUploadingAttachment"
        icon="i-lucide-paperclip"
        @click="openFileDialog"
      />
      <span
        v-if="isRecording"
        class="mx-2 text-xs tabular-nums text-n-ruby-11 animate-pulse"
      >
        {{ recordingLabel }}
      </span>
      <input
        v-model="newMessage"
        class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        @keydown.enter.exact="handleEnterKey"
      />
      <NextButton
        v-if="canRecordAudio"
        ghost
        sm
        :slate="!isRecording"
        :ruby="isRecording"
        :disabled="isUploadingAttachment"
        :icon="isRecording ? 'i-lucide-square' : 'i-lucide-mic'"
        :title="
          isRecording
            ? t('CAPTAIN.PLAYGROUND.AUDIO.STOP')
            : t('CAPTAIN.PLAYGROUND.AUDIO.RECORD')
        "
        @click="isRecording ? stopRecording() : startRecording()"
      />
      <NextButton
        ghost
        sm
        :disabled="(!newMessage.trim() && !pendingAttachment) || isRecording"
        icon="i-lucide-send"
        @click="sendMessage"
      />
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('CAPTAIN.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
